/*
 * (C) 2026 BeyondDemise, All Rights Reserved.
 *
 * UI i18n service (#89) — thin `t(key)` wrapper over per-locale message
 * catalogs (strategy option (c) from the issue; rationale + how-to in
 * docs/i18n.md).
 *
 *   - English (`src/i18n/en.ts`) is the statically imported source of
 *     truth; other locales lazy-load as their own chunk, mirroring the
 *     per-language wordlist code-splitting (F10).
 *   - The active locale persists in localStorage and survives reloads;
 *     `initUiLocale()` (called from `main.ts`) restores it at boot.
 *   - Locale switches dispatch `LOCALE_CHANGED_EVENT` on `document`;
 *     Lit components re-render via `LocaleController` (a
 *     ReactiveController — one field per component).
 *   - Unknown locales and missing keys fall back to English, never
 *     throw: a UI string must not take the app down.
 */

import { en, type MessageKey, type UiMessages } from '../i18n/en.js';

/** Locales with a complete UI message catalog. */
export const SUPPORTED_UI_LOCALES = ['en', 'de'] as const;
export type UiLocale = (typeof SUPPORTED_UI_LOCALES)[number];

/** localStorage key persisting the user's UI-locale choice. */
export const UI_LOCALE_STORAGE_KEY = 'bd.uiLocale';

/** Dispatched on `document` after the active locale (and catalog) changed. */
export const LOCALE_CHANGED_EVENT = 'bd-locale-changed';

/** Detail payload of {@link LOCALE_CHANGED_EVENT}. */
export interface LocaleChangedDetail {
  locale: UiLocale;
}

/** Type guard: is `value` a locale we ship a UI catalog for? */
export const isUiLocale = (value: string): value is UiLocale =>
  (SUPPORTED_UI_LOCALES as readonly string[]).includes(value);

/**
 * Per-locale catalog loaders. Non-English catalogs use dynamic import
 * so each ships as its own lazy chunk (F10 pattern).
 */
const loaders: Record<UiLocale, () => Promise<UiMessages>> = {
  en: () => Promise.resolve(en),
  de: async () => (await import('../i18n/de.js')).de,
};

let activeLocale: UiLocale = 'en';
let activeMessages: UiMessages = en;

/**
 * Translate a message key in the active locale.
 *
 * `{param}` placeholders are substituted from `params`. A key missing
 * from the active catalog falls back to English (type-parity makes
 * that unreachable for compiled locales, but guards hand-edited
 * catalogs at runtime).
 *
 * @param key message key from the `en` catalog
 * @param params placeholder name → substitution value
 * @returns the translated, substituted string
 */
export const t = (
  key: MessageKey,
  params?: Readonly<Record<string, string | number>>
): string => {
  let message: string = activeMessages[key] ?? en[key];
  if (params) {
    for (const [name, value] of Object.entries(params)) {
      message = message.replaceAll(`{${name}}`, String(value));
    }
  }
  return message;
};

/** The currently active UI locale. */
export const getUiLocale = (): UiLocale => activeLocale;

/**
 * Switch the active UI locale: lazy-load the catalog, persist the
 * choice, and notify components via {@link LOCALE_CHANGED_EVENT}.
 * Unsupported values are ignored (the UI stays on the current locale)
 * so wordlist-only languages can share a selector with the UI locale.
 *
 * @param locale one of {@link SUPPORTED_UI_LOCALES}
 */
export const setUiLocale = async (locale: string): Promise<void> => {
  if (!isUiLocale(locale)) return;
  if (locale === activeLocale) return;
  activeMessages = await loaders[locale]();
  activeLocale = locale;
  try {
    localStorage.setItem(UI_LOCALE_STORAGE_KEY, locale);
  } catch {
    // Storage unavailable (private mode/quota) — the choice simply
    // doesn't survive a reload.
  }
  document.dispatchEvent(
    new CustomEvent<LocaleChangedDetail>(LOCALE_CHANGED_EVENT, {
      detail: { locale },
    })
  );
};

/**
 * Restore the persisted UI locale at boot (no-op when nothing was
 * persisted or the stored value is not a supported locale).
 */
export const initUiLocale = async (): Promise<void> => {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(UI_LOCALE_STORAGE_KEY);
  } catch {
    return;
  }
  if (stored && isUiLocale(stored)) {
    await setUiLocale(stored);
  }
};

/**
 * Test seam: reset to English without persisting or announcing.
 * Production code must use {@link setUiLocale}.
 */
export const resetUiLocaleForTests = (): void => {
  activeLocale = 'en';
  activeMessages = en;
  try {
    localStorage.removeItem(UI_LOCALE_STORAGE_KEY);
  } catch {
    // ignore
  }
};

/**
 * Minimal ReactiveControllerHost surface the controller needs —
 * matches LitElement without importing lit here.
 */
interface LocaleControllerHost {
  addController(controller: {
    hostConnected?(): void;
    hostDisconnected?(): void;
  }): void;
  requestUpdate(): void;
}

/**
 * Lit ReactiveController that re-renders its host when the UI locale
 * changes. Usage inside a component:
 *
 * ```ts
 * readonly localeController = new LocaleController(this);
 * ```
 */
export class LocaleController {
  private readonly host: LocaleControllerHost;

  private readonly _onLocaleChanged = (): void => {
    this.host.requestUpdate();
  };

  constructor(host: LocaleControllerHost) {
    this.host = host;
    host.addController(this);
  }

  hostConnected(): void {
    document.addEventListener(LOCALE_CHANGED_EVENT, this._onLocaleChanged);
  }

  hostDisconnected(): void {
    document.removeEventListener(LOCALE_CHANGED_EVENT, this._onLocaleChanged);
  }
}

export type { MessageKey } from '../i18n/en.js';
